import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import Stack from "@mui/material/Stack";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Slider from "@mui/material/Slider";
import Divider from "@mui/material/Divider";
import { Close as CloseIcon } from "@mui/icons-material";
import { ResizableSplitter } from "../ResizableSplitter";
import { PatrolList } from "./PatrolList";
import { PatrolViewer } from "./PatrolViewer";
import PatrolImageViewer from "./PatrolImageViewer";
import { safeGetItem, safeSetItem } from "../../../utils/storage";
import { loadFooterSettings } from "../../../services/settings/GlobalSettingsService";
import { post, API_BASE_URL } from "../../../services/api";
import { userDisplaySettingsAPI, sessionAPI } from "../../../services/api";
import { usePdfToImageUrl } from "../../../hooks/usePdfToImageUrl";
import ImageIcon from "@mui/icons-material/Image";
import toast from "react-hot-toast";
import patrolListFixture from "./patrolListFixture.json";
import { resolvePatrolMediaUrl } from "./patrolMediaUrls";
import { loadPatrolLiveMonitoringContext } from "./patrolLiveMonitoring.js";
import {
  DEFAULT_PATROL_ROTATION_DURATION_SECONDS,
  normalizePatrolRotationDurationSeconds,
  PATROL_ROTATION_DURATION_MIN_SEC,
  PATROL_ROTATION_DURATION_MAX_SEC,
} from "./patrolRotationSpeed";
import {
  PATROL_PTZ_PREVIEW_SHARE_DEFAULT,
  PATROL_PTZ_PREVIEW_SHARE_MAX,
  PATROL_PTZ_PREVIEW_SHARE_MIN,
  normalizePatrolPtzPreviewSharePct,
} from "./patrolPtzPreviewSize";
// import CloseIcon from "@mui/icons-material/Close";

const USE_PATROL_LIST_FIXTURE = true;

/** Keeps patrol list width within ResizableSplitter min/max % (avoids huge empty gutter from bad localStorage). */
const clampPatrolSplitPercent = (value) => {
  const n = Number(value);
  const base = Number.isFinite(n) && n > 0 ? n : 35;
  return Math.min(70, Math.max(25, base));
};

const DEFAULT_ROTATION_DURATION_SECONDS = DEFAULT_PATROL_ROTATION_DURATION_SECONDS;

const normalizeRotationDurationSeconds = normalizePatrolRotationDurationSeconds;

export function Patrol({
  selectedDrawingForView,
  refreshKey = 0,
  onSelectDrawing,
  onClearSelectedDrawing,
  /** Full-width 360 / video player only (no patrol list). */
  variant = "default",
}) {
  // State for patrol list
  const [searchQuery, setSearchQuery] = useState("");
  const [dateFilter, setDateFilter] = useState("Today");
  const [patrols, setPatrols] = useState([]);
  const [dateRange, setDateRange] = useState(() => {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, "0");
    const dd = String(today.getDate()).padStart(2, "0");
    const todayStr = `${yyyy}-${mm}-${dd}`;
    return { from: todayStr, to: todayStr };
  });

  const [selectedPatrol, setSelectedPatrol] = useState(null);
  const [selectedSessionId, setSelectedSessionId] = useState(null);
  const [activeButton, setActiveButton] = useState("list");
  const [loader, setLoader] = useState(false); // 'menu', 'filter', or 'view'
  const [showFooter, setShowFooter] = useState(true);
  const [totalCount, setTotalCount] = useState(null);

  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [selectedPatrolId, setSelectedPatrolId] = useState(null);
  // This will come from API later

  useEffect(() => {
    const loadFooter = async () => {
      try {
        const settings = await loadFooterSettings();
        setShowFooter(settings?.showFooter !== false);
      } catch {
        setShowFooter(true);
      }
    };
    loadFooter();
    const onFooterUpdated = () => loadFooter();
    window.addEventListener("footerSettingsUpdated", onFooterUpdated);
    return () =>
      window.removeEventListener("footerSettingsUpdated", onFooterUpdated);
  }, []);
  const currentCount =
    activeButton === "list"
      ? patrols.length
      : patrols?.reduce((acc, patrol) => acc + patrol?.sessions?.length, 0);

  // Settings state
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [columnVisibility, setColumnVisibility] = useState({
    id: true,
    action: true,
    // Date, Name, and Session are mandatory (always visible)
  });
  const [tempColumnVisibility, setTempColumnVisibility] = useState({
    id: true,
    action: true,
  });
  const [defaultDateFilter, setDefaultDateFilter] = useState("Today");
  const [tempDefaultDateFilter, setTempDefaultDateFilter] = useState("Today");
  const [autoPlayVideo, setAutoPlayVideo] = useState(true);
  const [tempAutoPlayVideo, setTempAutoPlayVideo] = useState(true);
  const [rotationDurationSeconds, setRotationDurationSeconds] = useState(
    DEFAULT_ROTATION_DURATION_SECONDS,
  );
  const [tempRotationDurationSeconds, setTempRotationDurationSeconds] =
    useState(DEFAULT_ROTATION_DURATION_SECONDS);
  const [ptzPreviewSharePct, setPtzPreviewSharePct] = useState(
    PATROL_PTZ_PREVIEW_SHARE_DEFAULT,
  );
  const [tempPtzPreviewSharePct, setTempPtzPreviewSharePct] = useState(
    PATROL_PTZ_PREVIEW_SHARE_DEFAULT,
  );
  const [savingSettings, setSavingSettings] = useState(false);

  // Image viewer state
  const [isImageViewerOpen, setIsImageViewerOpen] = useState(false);
  const [imageUrl, setImageUrl] = useState(null);
  const { imageUrl: displayImageUrl, loading: pdfLoading } =
    usePdfToImageUrl(imageUrl);
  const [pins, setPins] = useState([]);
  const [liveMonitoringLoading, setLiveMonitoringLoading] = useState(
    () => variant === "liveMonitoring",
  );

  const dateFilterOptions = [
    "Today",
    "Yesterday",
    "Last 7 Days",
    "Last 30 Days",
    "This Week",
    "This Month",
  ];
  // Resizable splitter state (percent width of list column; clamped to 25–70)
  const savedWidth = safeGetItem("patrol-splitter-width", "35");
  const [leftPanelWidth, setLeftPanelWidthState] = useState(() =>
    clampPatrolSplitPercent(savedWidth),
  );
  const setLeftPanelWidth = useCallback((next) => {
    setLeftPanelWidthState(clampPatrolSplitPercent(next));
  }, []);
  // Save width to localStorage when it changes
  useEffect(() => {
    safeSetItem("patrol-splitter-width", leftPanelWidth.toString());
  }, [leftPanelWidth]);

  /**
   * Load patrol settings from API on mount, fallback to localStorage
   */
  useEffect(() => {
    const loadPatrolSettings = async () => {
      try {
        // Try to fetch from API first
        const response = await userDisplaySettingsAPI.get("patrol");

        // Handle different response structures
        const apiSettings = response?.data || response || {};

        // Check if we have valid settings data
        if (
          apiSettings.showId !== undefined ||
          apiSettings.showAction !== undefined ||
          apiSettings.defaultDateFilter !== undefined ||
          apiSettings.autoPlayVideo !== undefined ||
          apiSettings.rotationDurationSeconds !== undefined ||
          apiSettings.ptzPreviewSharePct !== undefined
        ) {
          // Map API response to column visibility format
          const visibility = {
            id: apiSettings.showId !== undefined ? apiSettings.showId : true,
            action:
              apiSettings.showAction !== undefined
                ? apiSettings.showAction
                : true,
          };
          setColumnVisibility(visibility);

          // Load default date filter
          if (
            apiSettings.defaultDateFilter !== undefined &&
            dateFilterOptions.includes(apiSettings.defaultDateFilter)
          ) {
            setDefaultDateFilter(apiSettings.defaultDateFilter);
            localStorage.setItem(
              "patrolManagement_defaultDateFilter",
              apiSettings.defaultDateFilter,
            );
          }

          // Load auto-play video setting
          if (apiSettings.autoPlayVideo !== undefined) {
            setAutoPlayVideo(apiSettings.autoPlayVideo);
            localStorage.setItem(
              "patrolManagement_autoPlayVideo",
              JSON.stringify(apiSettings.autoPlayVideo),
            );
          }

          if (apiSettings.rotationDurationSeconds !== undefined) {
            const safeRotationSeconds = normalizeRotationDurationSeconds(
              apiSettings.rotationDurationSeconds,
            );
            setRotationDurationSeconds(safeRotationSeconds);
            localStorage.setItem(
              "patrolManagement_rotationDurationSeconds",
              String(safeRotationSeconds),
            );
          }

          if (apiSettings.ptzPreviewSharePct !== undefined) {
            const safeShare = normalizePatrolPtzPreviewSharePct(
              apiSettings.ptzPreviewSharePct,
            );
            setPtzPreviewSharePct(safeShare);
            localStorage.setItem(
              "patrolManagement_ptzPreviewSharePct",
              String(safeShare),
            );
          }

          // Also update localStorage to keep it in sync
          localStorage.setItem(
            "patrolManagement_columnVisibility",
            JSON.stringify(visibility),
          );
          return; // Successfully loaded from API
        }

        // If API response doesn't have the expected structure, fall through to localStorage
        throw new Error("No valid API data");
      } catch (error) {
        console.error("Error loading patrol settings from API:", error);
        // Fallback to localStorage
        const savedVisibility = localStorage.getItem(
          "patrolManagement_columnVisibility",
        );
        if (savedVisibility) {
          try {
            const parsed = JSON.parse(savedVisibility);
            setColumnVisibility({
              ...parsed,
              path: false,
            });
          } catch (e) {
            console.error(
              "Error loading patrol settings from localStorage:",
              e,
            );
            // Use default values if both fail
            setColumnVisibility({
              id: true,
              action: true,
            });
          }
        } else {
          // Use default values if no saved settings
          setColumnVisibility({
            id: true,
            action: true,
          });
        }

        // Load default date filter from localStorage
        const savedDefaultDateFilter = localStorage.getItem(
          "patrolManagement_defaultDateFilter",
        );
        if (
          savedDefaultDateFilter &&
          dateFilterOptions.includes(savedDefaultDateFilter)
        ) {
          setDefaultDateFilter(savedDefaultDateFilter);
        }

        // Load auto-play video setting from localStorage
        const savedAutoPlayVideo = localStorage.getItem(
          "patrolManagement_autoPlayVideo",
        );
        if (savedAutoPlayVideo !== null) {
          try {
            setAutoPlayVideo(JSON.parse(savedAutoPlayVideo));
          } catch (e) {
            console.error(
              "Error loading auto-play video setting from localStorage:",
              e,
            );
          }
        }

        const savedRotationDurationSeconds = localStorage.getItem(
          "patrolManagement_rotationDurationSeconds",
        );
        if (savedRotationDurationSeconds != null) {
          setRotationDurationSeconds(
            normalizeRotationDurationSeconds(savedRotationDurationSeconds),
          );
        }

        const savedPtzPreviewSharePct = localStorage.getItem(
          "patrolManagement_ptzPreviewSharePct",
        );
        if (savedPtzPreviewSharePct != null) {
          setPtzPreviewSharePct(
            normalizePatrolPtzPreviewSharePct(savedPtzPreviewSharePct),
          );
        }
      }
    };

    loadPatrolSettings();
  }, []);

  /**
   * Apply default date filter on mount (only once).
   * Kept for backward compatibility with settings but skipped —
   * the calendar picker always initialises with today.
   */
  useEffect(() => {
    if (false && defaultDateFilter && defaultDateFilter !== "Today") {
      // Import getDateRange function logic or use the one from PatrolListHeader
      const applyDefaultDateFilter = (option) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        let fromDate, toDate;

        switch (option) {
          case "Today": {
            fromDate = new Date(today);
            toDate = new Date(today);
            break;
          }
          case "Yesterday": {
            const yesterday = new Date(today);
            yesterday.setDate(today.getDate() - 1);
            fromDate = new Date(yesterday);
            toDate = new Date(yesterday);
            break;
          }
          case "Last 7 Days": {
            const sevenDaysAgo = new Date(today);
            sevenDaysAgo.setDate(today.getDate() - 7);
            fromDate = new Date(sevenDaysAgo);
            toDate = new Date(today);
            break;
          }
          case "Last 30 Days": {
            const thirtyDaysAgo = new Date(today);
            thirtyDaysAgo.setDate(today.getDate() - 30);
            fromDate = new Date(thirtyDaysAgo);
            toDate = new Date(today);
            break;
          }
          case "This Week": {
            const dayOfWeek = today.getDay();
            const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
            const startOfWeek = new Date(today);
            startOfWeek.setDate(today.getDate() + diff);
            const endOfWeek = new Date(startOfWeek);
            endOfWeek.setDate(startOfWeek.getDate() + 6);
            fromDate = new Date(startOfWeek);
            toDate = new Date(endOfWeek);
            break;
          }
          case "This Month": {
            const startOfMonth = new Date(
              today.getFullYear(),
              today.getMonth(),
              1,
            );
            const endOfMonth = new Date(
              today.getFullYear(),
              today.getMonth() + 1,
              0,
            );
            fromDate = new Date(startOfMonth);
            toDate = new Date(endOfMonth);
            break;
          }
          default: {
            fromDate = new Date(today);
            toDate = new Date(today);
          }
        }

        const formatDate = (date) => {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, "0");
          const day = String(date.getDate()).padStart(2, "0");
          return `${year}-${month}-${day}`;
        };

        return {
          fromDate: formatDate(fromDate),
          toDate: formatDate(toDate),
        };
      };

      const { fromDate, toDate } = applyDefaultDateFilter(defaultDateFilter);
      setDateFilter(defaultDateFilter);
      setDateRange({
        from: fromDate,
        to: toDate,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultDateFilter]); // Run when defaultDateFilter is loaded from settings

  /**
   * Save patrol settings to localStorage when it changes
   */
  useEffect(() => {
    localStorage.setItem(
      "patrolManagement_columnVisibility",
      JSON.stringify(columnVisibility),
    );
  }, [columnVisibility]);

  /**
   * Initialize temp patrol settings when settings dialog opens
   */
  useEffect(() => {
    if (isSettingsOpen) {
      setTempColumnVisibility(columnVisibility);
      setTempDefaultDateFilter(defaultDateFilter);
      setTempAutoPlayVideo(autoPlayVideo);
      setTempRotationDurationSeconds(rotationDurationSeconds);
      setTempPtzPreviewSharePct(ptzPreviewSharePct);
    }
  }, [
    isSettingsOpen,
    columnVisibility,
    defaultDateFilter,
    autoPlayVideo,
    rotationDurationSeconds,
    ptzPreviewSharePct,
  ]);

  // Debounced search query state
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");

  const drawingNameForFilter = selectedDrawingForView?.name
    ? selectedDrawingForView.name.replace(/\.(pdf|dwg|dxf)$/i, "")
    : null;

  // Debounce search query with 300ms delay
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch patrols data on component mount
  useEffect(() => {
    const fetchPatrols = async () => {
      if (USE_PATROL_LIST_FIXTURE) {
        setLoader(true);
        try {
          const payload = patrolListFixture;
          const rows = Array.isArray(payload?.data) ? payload.data : [];
          setPatrols(rows);
          setTotalCount(
            typeof payload?.totalCount === "number"
              ? payload.totalCount
              : rows.length,
          );
        } catch (error) {
          console.error("Error loading patrol list fixture:", error);
          setPatrols([]);
          setTotalCount(0);
        } finally {
          setLoader(false);
        }
        return;
      }

      if (!selectedDrawingForView?.id) {
        setPatrols([]);
        setTotalCount(0);
        setLoader(false);
        return;
      }

      setLoader(true);
      try {
        const response = await post("/patrol", {
          from: dateRange.from,
          to: dateRange.to,
          querytype: activeButton,
          searchQuery: debouncedSearchQuery,
          directory_id: selectedDrawingForView.id,
          drawingName: drawingNameForFilter,
        });
        // Handle response - adjust based on actual API response structure
        if (response && response.data) {
          setPatrols(Array.isArray(response.data) ? response.data : []);
          setTotalCount(response?.totalCount);
          setLoader(false);
        } else if (Array.isArray(response)) {
          setPatrols(response);
          setLoader(false);
        } else {
          setPatrols([]);
          setLoader(false);
        }
      } catch (error) {
        console.error("Error fetching patrols:", error);
        setPatrols([]);
        setLoader(false);
      }
    };

    fetchPatrols();
  }, [
    dateRange,
    activeButton,
    debouncedSearchQuery,
    selectedDrawingForView?.id,
    drawingNameForFilter,
    refreshKey,
  ]);

  useEffect(() => {
    setSelectedPatrol(null);
    setSelectedPatrolId(null);
    setSelectedSessionId(null);
    setPins([]);
    setImageUrl(null);
    setCurrentVideoIndex(0);
  }, [selectedDrawingForView?.id]);

  // Handlers for search and filters
  const handleSearchChange = (value) => {
    setSearchQuery(value);
    // TODO: Implement search logic
  };

  const handleDateFilterChange = (value, fromDate, toDate) => {
    setDateFilter(value);
    setDateRange({
      from: fromDate,
      to: toDate,
    });
    // TODO: Implement date filter logic with fromDate and toDate
  };

  // Format date to YYYY-MM-DD string (handles both Date objects and strings)
  const formatDateToString = (date) => {
    if (!date) return "";
    if (typeof date === "string") return date;
    if (date instanceof Date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    }
    return "";
  };

  const handleMenuClick = () => {
    setActiveButton("list");
    toast.success("Patrol list displayed in List View.");
    // TODO: Open menu
  };

  const handleFilterClick = () => {
    setActiveButton("path");
    toast.success("Patrol list grouped by drawing path.");
    // TODO: Open filter menu
  };

  const handleViewClick = () => {
    toast.success("Patrol list grouped by filename.");
    setActiveButton("filename");
    // TODO: Toggle view mode
  };

  const handlePatrolClick = useCallback(
    async (patrol, session_id, _patrol_id) => {
      if (USE_PATROL_LIST_FIXTURE && session_id) {
        const recordings = Array.isArray(patrol) ? patrol : [];
        setImageUrl(
          resolvePatrolMediaUrl(
            "__patrol_public__/images/login-background2.jpg",
          ),
        );
        setPins([]);
        setSelectedPatrol(recordings);
        setSelectedPatrolId(_patrol_id ?? session_id);
        setSelectedSessionId(session_id);
        return;
      }

      if (session_id) {
        try {
          const response = await sessionAPI.getMediaPins(session_id);

          if (response?.data?.path && response?.data?.orig_filename) {
            const imageUrl = `${API_BASE_URL}/${response.data.path}/orig_${response.data.media_id}.${response.data.extension}`;

            setImageUrl(imageUrl);
          }
          if (response?.data?.pins?.length > 0) {
            setPins(response?.data?.pins);
            setSelectedPatrol(patrol);
            setSelectedPatrolId(_patrol_id ?? session_id);
            setSelectedSessionId(session_id);
          }
        } catch (error) {
          console.error("Error fetching media pins:", error);
        }
      }
    },
    [],
  );

  /** Live monitoring: Patrol Settings drawing + camera placements + RTSP + hotspots. */
  useEffect(() => {
    if (variant !== "liveMonitoring") {
      setLiveMonitoringLoading(false);
      return;
    }

    let cancelled = false;
    setLiveMonitoringLoading(true);
    (async () => {
      try {
        const ctx = await loadPatrolLiveMonitoringContext();
        if (cancelled) return;

        if (ctx.drawingImageUrl) {
          setImageUrl(ctx.drawingImageUrl);
        }

        if (ctx.patrolRecordings.length > 0) {
          setSelectedPatrol(ctx.patrolRecordings);
          setCurrentVideoIndex(0);
          setSelectedSessionId("live-monitoring");
          setSelectedPatrolId(ctx.drawing?.id ?? "live-monitoring");
        } else {
          setSelectedPatrol([]);
          if (!ctx.drawingImageUrl) {
            toast.error(
              "No patrol drawing or camera placements found. Configure them in Administration → Patrol setting.",
            );
          } else {
            toast.error(
              "Drawing loaded but no cameras are placed. Add camera locations in Patrol setting.",
            );
          }
        }
      } catch (err) {
        if (!cancelled) {
          console.error("Live monitoring setup failed:", err);
          toast.error(
            err?.message || "Could not load live monitoring patrol data.",
          );
        }
      } finally {
        if (!cancelled) setLiveMonitoringLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [variant]);

  const liveHlsUrlForPlayer = React.useMemo(() => {
    if (variant !== "liveMonitoring") return null;
    const rec = selectedPatrol?.[currentVideoIndex];
    const candidates = [
      rec?.live_hls_url,
      rec?.live_stream_kind === "ptz"
        ? rec?.live_hls_ptz_url
        : rec?.live_hls_360_url,
      rec?.live_hls_ptz_url,
      rec?.live_hls_360_url,
      rec?.recording_path,
    ];
    for (const url of candidates) {
      const s = String(url ?? "").trim();
      if (s && /\.m3u8(\?|$)/i.test(s)) return s;
    }
    return null;
  }, [variant, selectedPatrol, currentVideoIndex]);

  const handleActionClick = (patrol, event) => {
    // TODO: Implement action menu
    console.log("Action clicked for patrol:", patrol.id);
  };

  const handleResetPatrolSettings = () => {
    setTempColumnVisibility({
      id: true,
      action: true,
    });
    setTempDefaultDateFilter("Today");
    setTempAutoPlayVideo(false);
    setTempRotationDurationSeconds(DEFAULT_ROTATION_DURATION_SECONDS);
    setTempPtzPreviewSharePct(PATROL_PTZ_PREVIEW_SHARE_DEFAULT);
  };

  const handleCancelPatrolSettings = () => {
    setIsSettingsOpen(false);
    // Reset temp visibility to current visibility (discard changes)
    setTempColumnVisibility(columnVisibility);
    setTempDefaultDateFilter(defaultDateFilter);
    setTempAutoPlayVideo(autoPlayVideo);
    setTempRotationDurationSeconds(rotationDurationSeconds);
    setTempPtzPreviewSharePct(ptzPreviewSharePct);
  };

  const handleApplyPatrolSettings = async () => {
    setSavingSettings(true);
    try {
      // Map patrol settings to API payload format
      const payload = {
        showId: tempColumnVisibility.id,
        showPath: false,
        showAction: tempColumnVisibility.action,
        defaultDateFilter: tempDefaultDateFilter,
        autoPlayVideo: tempAutoPlayVideo,
        rotationDurationSeconds: normalizeRotationDurationSeconds(
          tempRotationDurationSeconds,
        ),
        ptzPreviewSharePct: normalizePatrolPtzPreviewSharePct(
          tempPtzPreviewSharePct,
        ),
      };

      // Call API to save patrol settings
      await userDisplaySettingsAPI.save("patrol", payload);

      // Update local state
      setColumnVisibility({
        ...tempColumnVisibility,
        path: false,
      });
      setDefaultDateFilter(tempDefaultDateFilter);
      setAutoPlayVideo(tempAutoPlayVideo);
      setRotationDurationSeconds(
        normalizeRotationDurationSeconds(tempRotationDurationSeconds),
      );
      setPtzPreviewSharePct(
        normalizePatrolPtzPreviewSharePct(tempPtzPreviewSharePct),
      );

      // Update localStorage to keep it in sync
      localStorage.setItem(
        "patrolManagement_columnVisibility",
        JSON.stringify({
          ...tempColumnVisibility,
          path: false,
        }),
      );
      localStorage.setItem(
        "patrolManagement_defaultDateFilter",
        tempDefaultDateFilter,
      );
      localStorage.setItem(
        "patrolManagement_autoPlayVideo",
        JSON.stringify(tempAutoPlayVideo),
      );
      localStorage.setItem(
        "patrolManagement_rotationDurationSeconds",
        String(normalizeRotationDurationSeconds(tempRotationDurationSeconds)),
      );
      localStorage.setItem(
        "patrolManagement_ptzPreviewSharePct",
        String(normalizePatrolPtzPreviewSharePct(tempPtzPreviewSharePct)),
      );

      // Apply the new default date filter if it changed
      if (tempDefaultDateFilter !== dateFilter) {
        const applyDefaultDateFilter = (option) => {
          const today = new Date();
          today.setHours(0, 0, 0, 0);

          let fromDate, toDate;

          switch (option) {
            case "Today": {
              fromDate = new Date(today);
              toDate = new Date(today);
              break;
            }
            case "Yesterday": {
              const yesterday = new Date(today);
              yesterday.setDate(today.getDate() - 1);
              fromDate = new Date(yesterday);
              toDate = new Date(yesterday);
              break;
            }
            case "Last 7 Days": {
              const sevenDaysAgo = new Date(today);
              sevenDaysAgo.setDate(today.getDate() - 7);
              fromDate = new Date(sevenDaysAgo);
              toDate = new Date(today);
              break;
            }
            case "Last 30 Days": {
              const thirtyDaysAgo = new Date(today);
              thirtyDaysAgo.setDate(today.getDate() - 30);
              fromDate = new Date(thirtyDaysAgo);
              toDate = new Date(today);
              break;
            }
            case "This Week": {
              const dayOfWeek = today.getDay();
              const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
              const startOfWeek = new Date(today);
              startOfWeek.setDate(today.getDate() + diff);
              const endOfWeek = new Date(startOfWeek);
              endOfWeek.setDate(startOfWeek.getDate() + 6);
              fromDate = new Date(startOfWeek);
              toDate = new Date(endOfWeek);
              break;
            }
            case "This Month": {
              const startOfMonth = new Date(
                today.getFullYear(),
                today.getMonth(),
                1,
              );
              const endOfMonth = new Date(
                today.getFullYear(),
                today.getMonth() + 1,
                0,
              );
              fromDate = new Date(startOfMonth);
              toDate = new Date(endOfMonth);
              break;
            }
            default: {
              fromDate = new Date(today);
              toDate = new Date(today);
            }
          }

          const formatDate = (date) => {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, "0");
            const day = String(date.getDate()).padStart(2, "0");
            return `${year}-${month}-${day}`;
          };

          return {
            fromDate: formatDate(fromDate),
            toDate: formatDate(toDate),
          };
        };

        const { fromDate, toDate } = applyDefaultDateFilter(
          tempDefaultDateFilter,
        );
        setDateFilter(tempDefaultDateFilter);
        setDateRange({
          from: fromDate,
          to: toDate,
        });
      }

      setIsSettingsOpen(false);
    } catch (error) {
      console.error("Error saving patrol settings:", error);
      // Still update local state and localStorage even if API fails
      setColumnVisibility({
        ...tempColumnVisibility,
        path: false,
      });
      setDefaultDateFilter(tempDefaultDateFilter);
      setAutoPlayVideo(tempAutoPlayVideo);
      setRotationDurationSeconds(
        normalizeRotationDurationSeconds(tempRotationDurationSeconds),
      );
      setPtzPreviewSharePct(
        normalizePatrolPtzPreviewSharePct(tempPtzPreviewSharePct),
      );
      localStorage.setItem(
        "patrolManagement_columnVisibility",
        JSON.stringify({
          ...tempColumnVisibility,
          path: false,
        }),
      );
      localStorage.setItem(
        "patrolManagement_defaultDateFilter",
        tempDefaultDateFilter,
      );
      localStorage.setItem(
        "patrolManagement_autoPlayVideo",
        JSON.stringify(tempAutoPlayVideo),
      );
      localStorage.setItem(
        "patrolManagement_rotationDurationSeconds",
        String(normalizeRotationDurationSeconds(tempRotationDurationSeconds)),
      );
      localStorage.setItem(
        "patrolManagement_ptzPreviewSharePct",
        String(normalizePatrolPtzPreviewSharePct(tempPtzPreviewSharePct)),
      );
      setIsSettingsOpen(false);
    } finally {
      setSavingSettings(false);
    }
  };

  const isLiveMonitoring = variant === "liveMonitoring";

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: isLiveMonitoring ? 0 : 0.75,
        px: isLiveMonitoring ? 0 : { xs: 2, sm: 2.5 },
        pt: isLiveMonitoring ? 0 : 2,
        pb: isLiveMonitoring ? 0 : 2,
        boxSizing: "border-box",
        ...(isLiveMonitoring && {
          flex: 1,
          minHeight: 0,
          height: "100%",
        }),
      }}
    >
      <motion.div
        style={{
          width: "100%",
          ...(isLiveMonitoring && {
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
          }),
        }}
        initial={{
          opacity: 0,
          y: 10,
        }}
        animate={{
          opacity: 1,
          y: 0,
        }}
        transition={{
          duration: 0.3,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            width: "100%",
            height: isLiveMonitoring
              ? "100%"
              : showFooter
                ? "calc(100vh - 195px)"
                : "814px",
            minHeight: isLiveMonitoring ? 0 : "600px",
            flex: isLiveMonitoring ? 1 : undefined,
            minWidth: 0,
            gap: 0,
            position: "relative",
            boxSizing: "border-box",
            alignItems: "stretch",
          }}
        >
          {/* Left Panel - Patrol List (hidden in live monitoring — full-width player) */}
          {!isLiveMonitoring ? (
            <ResizableSplitter
              storageKey="patrol-splitter-width"
              defaultWidth={35}
              minWidth="300px"
              maxWidth="70%"
              width={leftPanelWidth}
              setWidth={setLeftPanelWidth}
              enabled={true}
              handleClassName="resize-handle-patrol"
              minWidthPercent={25}
              maxWidthPercent={70}
            >
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                <AnimatePresence mode="wait">
                  {!isImageViewerOpen ? (
                    <motion.div
                      key="patrol-list"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{ duration: 0.3, ease: "easeInOut" }}
                      style={{
                        width: "100%",
                        height: "100%",
                        position: "absolute",
                        top: 0,
                        left: 0,
                      }}
                    >
                      <PatrolList
                        loader={loader}
                        totalCount={totalCount}
                        currentCount={currentCount}
                        selectedDrawingForView={selectedDrawingForView}
                        searchQuery={searchQuery}
                        onSearchChange={handleSearchChange}
                        dateFilter={dateFilter}
                        fromDate={formatDateToString(dateRange.from)}
                        toDate={formatDateToString(dateRange.to)}
                        onDateFilterChange={handleDateFilterChange}
                        onMenuClick={handleMenuClick}
                        onFilterClick={handleFilterClick}
                        onViewClick={handleViewClick}
                        activeButton={activeButton}
                        patrols={patrols}
                        selectedPatrolId={selectedSessionId}
                        onPatrolClick={handlePatrolClick}
                        onActionClick={handleActionClick}
                        columnVisibility={{
                          ...columnVisibility,
                          path: false,
                        }}
                        onSettingsClick={() => setIsSettingsOpen(true)}
                        isSettingsOpen={isSettingsOpen}
                        onSelectDrawing={onSelectDrawing}
                        onClearSelectedDrawing={onClearSelectedDrawing}
                        showListWithoutDrawing={USE_PATROL_LIST_FIXTURE}
                      />
                    </motion.div>
                  ) : (
                    <motion.div
                      key="image-viewer"
                      initial={{ opacity: 0, scale: 0.95, x: 20 }}
                      animate={{ opacity: 1, scale: 1, x: 0 }}
                      exit={{ opacity: 0, scale: 0.95, x: 20 }}
                      transition={{ duration: 0.3, ease: "easeInOut" }}
                      style={{
                        width: "100%",
                        height: "100%",
                        position: "absolute",
                        top: 0,
                        left: 0,
                      }}
                    >
                      <PatrolImageViewer
                        imageUrl={displayImageUrl}
                        pins={pins}
                        pdfLoading={pdfLoading}
                        onClose={() => {
                          setIsImageViewerOpen(false);
                          setImageUrl(null);
                        }}
                      />
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Toggle Button - Bottom Left Corner */}
                {/* <motion.div
                initial={false}
                animate={{
                  rotate: isImageViewerOpen ? 180 : 0,
                  scale: isImageViewerOpen ? 1.1 : 1,
                }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
                style={{
                  position: "absolute",
                  bottom: 16,
                  left: 16,
                  zIndex: 1000,
                }}
              >
                <IconButton
                  onClick={() => {
                    if (isImageViewerOpen) {
                      setIsImageViewerOpen(false);
                      // setImageUrl(null);
                    } else {
                      // Get image URL from selectedPatrol
                      // You can modify this to get the actual image URL from patrol data
                      // For now, assuming image path might be available or can be derived from recording_path
                      const defaultImageUrl = selectedPatrol?.length > 0 && selectedPatrol[0]?.recording_path
                        ? `${API_BASE_URL}/${selectedPatrol[0].recording_path.replace(/\.mp4$/, '.jpg')}`
                        : null;
                      if (defaultImageUrl) {
                        // setImageUrl(defaultImageUrl);
                        setIsImageViewerOpen(true);
                      } else {
                        // If no image URL available, you might want to show an error or use a default
                        console.warn("No image URL available for patrol");
                      }
                    }
                  }}
                  sx={{
                    backgroundColor: isImageViewerOpen ? "#FF6B35" : "rgba(255, 255, 255, 0.9)",
                    color: isImageViewerOpen ? "#FFFFFF" : "#4A5565",
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                    "&:hover": {
                      backgroundColor: isImageViewerOpen ? "#E55A2B" : "rgba(255, 255, 255, 1)",
                      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.2)",
                    },
                    transition: "all 0.2s ease",
                  }}
                  title={isImageViewerOpen ? "Close Image Viewer" : "Open Image Viewer"}
                >
                  <motion.div
                    animate={{
                      rotate: isImageViewerOpen ? 90 : 0,
                    }}
                    transition={{ duration: 0.3, ease: "easeInOut" }}
                  >
                    {isImageViewerOpen ? (
                      <CloseIcon fontSize="small" />
                    ) : (
                      <ImageIcon fontSize="small" />
                    )}
                  </motion.div>
                </IconButton>
              </motion.div> */}
              </Box>
            </ResizableSplitter>
          ) : null}

          {/* Right Panel - Patrol Viewer */}
          <Box
            sx={{
              flex: 1,
              height: "100%",
              minWidth: 0, // Important for flexbox
            }}
          >
            <PatrolViewer
              selectedPatrol={selectedPatrol}
              autoPlay={isLiveMonitoring ? true : autoPlayVideo}
              rotationDurationSeconds={rotationDurationSeconds}
              onSettingsClick={() => setIsSettingsOpen(true)}
              imageUrl={displayImageUrl}
              pins={pins}
              leftPanelWidth={isLiveMonitoring ? 0 : leftPanelWidth}
              selectedPatrolId={selectedPatrolId}
              currentVideoIndex={currentVideoIndex}
              setCurrentVideoIndex={setCurrentVideoIndex}
              showLiveMonitoringFloorControls={isLiveMonitoring}
              liveMonitoring={isLiveMonitoring}
              liveHlsUrl={liveHlsUrlForPlayer}
              liveMonitoringLoading={liveMonitoringLoading}
              ptzPreviewSharePct={
                isSettingsOpen
                  ? tempPtzPreviewSharePct
                  : ptzPreviewSharePct
              }
            />
          </Box>
        </Box>
      </motion.div>

      {/* Patrol Settings Dialog */}
      <Dialog
        open={isSettingsOpen}
        onClose={handleCancelPatrolSettings}
        maxWidth="sm"
        fullWidth
        sx={{
          "& .MuiDialog-paper": {
            color: "#212121",
            boxShadow: "var(--Paper-shadow)",
            backgroundImage: "var(--Paper-overlay)",
            width: "90vw",
            maxWidth: "560px",
            height: "auto",
            maxHeight: "90vh",
            display: "flex",
            flexDirection: "column",
            backgroundColor: "#FFFFFF",
            transition: "box-shadow 300ms cubic-bezier(0.4, 0, 0.2, 1)",
            borderRadius: "20px",
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            justifyContent: "space-between",
            display: "flex",
            p: 2,
            borderBottom: "1px solid #E5E7EB",
            mb: 2,
          }}
        >
          <Typography
            sx={{
              fontFamily: "Roboto, sans-serif",
              fontSize: 20,
              fontWeight: 700,
              color: "#ff8555",
            }}
          >
            Patrol Settings
          </Typography>
          <IconButton
            onClick={handleCancelPatrolSettings}
            size="small"
            sx={{
              color: "#4A5565",
              "&:hover": {
                backgroundColor: "#E5E7EB",
                color: "#2932E5",
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Box>
            <Typography
              sx={{
                fontFamily: "Roboto, sans-serif",
                fontSize: 14,
                fontWeight: 600,
                color: "#212121",
                mb: 1.5,
              }}
            >
              Patrol Rotation Speed
            </Typography>
            <TextField
              type="number"
              fullWidth
              size="small"
              label="Rotation speed (seconds per 360°)"
              value={tempRotationDurationSeconds}
              onChange={(e) => {
                const raw = e.target.value;
                if (raw === "") return;
                const n = Number(raw);
                if (!Number.isFinite(n)) return;
                setTempRotationDurationSeconds(n);
              }}
              onBlur={() => {
                setTempRotationDurationSeconds((prev) =>
                  normalizePatrolRotationDurationSeconds(prev),
                );
              }}
              inputProps={{
                min: PATROL_ROTATION_DURATION_MIN_SEC,
                max: PATROL_ROTATION_DURATION_MAX_SEC,
                step: 1,
              }}
              sx={{
                fontFamily: "Roboto, sans-serif",
                fontSize: 14,
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#E5E7EB",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#D1D5DB",
                },
                "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#2932E5",
                },
              }}
              helperText={`Enter ${PATROL_ROTATION_DURATION_MIN_SEC}–${PATROL_ROTATION_DURATION_MAX_SEC} seconds for one full auto-rotation cycle.`}
            />
          </Box>

          <Divider sx={{ my: 3, borderColor: "#E5E7EB" }} />

          <Box>
            <Typography
              sx={{
                fontFamily: "Roboto, sans-serif",
                fontSize: 14,
                fontWeight: 600,
                color: "#212121",
                mb: 1,
              }}
            >
              Small preview size ({tempPtzPreviewSharePct}% of main)
            </Typography>
            <Slider
              value={tempPtzPreviewSharePct}
              min={PATROL_PTZ_PREVIEW_SHARE_MIN}
              max={PATROL_PTZ_PREVIEW_SHARE_MAX}
              step={1}
              onChange={(_e, value) =>
                setTempPtzPreviewSharePct(
                  normalizePatrolPtzPreviewSharePct(value),
                )
              }
              valueLabelDisplay="auto"
              sx={{
                color: "#2932E5",
                "& .MuiSlider-thumb": {
                  bgcolor: "#2932E5",
                },
                "& .MuiSlider-track": {
                  bgcolor: "#2932E5",
                },
              }}
            />
            <Typography
              sx={{
                fontFamily: "Roboto, sans-serif",
                fontSize: 12,
                color: "#4A5565",
                mt: 0.5,
              }}
            >
              Width is {tempPtzPreviewSharePct}% of the main view; height follows
              a 16:9 PTZ frame so the preview is not squashed. Sizes adapt to
              browser, resolution, and fullscreen.
            </Typography>
          </Box>

          <Divider sx={{ my: 3, borderColor: "#E5E7EB" }} />


          <Box>
            <Typography
              sx={{
                fontFamily: "Roboto, sans-serif",
                fontSize: 14,
                fontWeight: 600,
                color: "#212121",
                mb: 1.5,
              }}
            >
              Default Date Filter
            </Typography>
            <FormControl fullWidth size="small">
              <InputLabel
                sx={{
                  fontFamily: "Roboto, sans-serif",
                  fontSize: 14,
                }}
              >
                Select default date filter
              </InputLabel>
              <Select
                value={tempDefaultDateFilter}
                onChange={(e) => setTempDefaultDateFilter(e.target.value)}
                label="Select default date filter"
                sx={{
                  fontFamily: "Roboto, sans-serif",
                  fontSize: 14,
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#E5E7EB",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#D1D5DB",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#2932E5",
                  },
                }}
              >
                {dateFilterOptions.map((option) => (
                  <MenuItem
                    key={option}
                    value={option}
                    sx={{
                      fontFamily: "Roboto, sans-serif",
                      fontSize: 14,
                    }}
                  >
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography
              sx={{
                fontFamily: "Roboto, sans-serif",
                fontSize: 12,
                color: "#4A5565",
                mt: 1,
              }}
            >
              Set the default date filter that will be applied when the page
              loads
            </Typography>
          </Box>

          <Divider sx={{ my: 3, borderColor: "#E5E7EB" }} />

          <Box>
            <FormControlLabel
              control={
                <Switch
                  checked={tempAutoPlayVideo}
                  onChange={(e) => setTempAutoPlayVideo(e.target.checked)}
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": {
                      color: "#2932E5",
                    },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: "#2932E5",
                    },
                  }}
                />
              }
              label={
                <Typography
                  sx={{
                    fontFamily: "Roboto, sans-serif",
                    fontSize: 14,
                    color: "#212121",
                  }}
                >
                  Auto-play video when row is clicked
                </Typography>
              }
              sx={{ m: 0 }}
            />
            <Typography
              sx={{
                fontFamily: "Roboto, sans-serif",
                fontSize: 12,
                color: "#4A5565",
                ml: 6.5,
                mt: 0.5,
              }}
            >
              Automatically start playing the video when a patrol row is
              selected
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            justifyContent: "space-between",
            px: 3,
            pb: 2,
            pt: 2,
            borderTop: "1px solid #E5E7EB",
          }}
        >
          <Button
            onClick={handleResetPatrolSettings}
            sx={{
              textTransform: "none",
              fontFamily: "Roboto, sans-serif",
              fontSize: 14,
              color: "#4a5565",
              "&:hover": {
                bgcolor: "#F3F4F6",
              },
            }}
          >
            Reset to Defaults
          </Button>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              onClick={handleCancelPatrolSettings}
              sx={{
                textTransform: "none",
                fontFamily: "Roboto, sans-serif",
                fontSize: 14,
                color: "#4a5565",
                "&:hover": {
                  bgcolor: "#F3F4F6",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleApplyPatrolSettings}
              variant="contained"
              disabled={savingSettings}
              sx={{
                textTransform: "none",
                fontFamily: "Roboto, sans-serif",
                fontSize: 14,
                bgcolor: "#2932E5",
                "&:hover": {
                  bgcolor: "#1e24b8",
                },
                "&:disabled": {
                  bgcolor: "#E5E7EB",
                  color: "#9CA3AF",
                },
              }}
            >
              {savingSettings ? "Applying..." : "Apply"}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
